/**
 * useTutorStudentsUnreadFirst — l'ordre de « Mes élèves » : « À traiter » d'abord (`unreadFirst`).
 *
 * Un élève dont des messages attendent passe en tête et y reste une fois sa conversation lue, tant que la liste
 * n'est pas relue (`release`, au retour sur l'onglet et à « Relire ») : au large, sa ligne et sa liste d'activités
 * ne sautent pas plus bas pendant qu'on lit ses messages.
 */
import { useCallback, useMemo, useState } from "react"

import { unreadFirst } from "../../../../view-models/tutor/Students/tutorStudentsViewModel"
import { type UITutorStudent } from "../../../types/tutorEleves"

const NONE: ReadonlySet<string> = new Set()

export function useTutorStudentsUnreadFirst(students: UITutorStudent[]) {
    const [held, setHeld] = useState<ReadonlySet<string>>(NONE)

    // Tenus pendant le rendu, pas dans un effet : la liste ne se rend qu'une fois quand un élève se met à attendre.
    const waiting = useMemo(
        () => students.filter((student) => (student.unreadMessagesCount ?? 0) > 0).map((student) => String(student.id)),
        [students]
    )
    if (!waiting.every((id) => held.has(id))) setHeld(new Set([...held, ...waiting]))

    const release = useCallback(() => setHeld(NONE), [])
    const ordered = useMemo(() => unreadFirst(students, held), [held, students])
    return { students: ordered, release }
}
